import React, { useRef, useState, useMemo } from 'react';
import styles from './HATVQualificationActionModal.module.scss';
import { useTranslation } from 'react-i18next';
import dropdownArrowLight from '../../../assets/images/LightTheme/down_arrow_light.svg';
import dropdownArrowDark from '../../../assets/images/DarkTheme/down_arrow_dark.svg';
import { useAppSelector } from '../../../redux/hooks';
import { HATV_QUALIFICATION_ACTION } from '../../../constants/dataConstants';
import FormatterBar from '../../common/FormatterBar/FormaterBarWithLinkmodel/FormatterBar';
import Button from '../../common/Button/Button';
import useClickOutside from '../../CustomHooks/useClickOutside';
import {
  getFromLocalStorage,
  getTimePeriodOptionsTillCurrent,
  useProfileParams,
} from '../../../utils/common';
import { useUpdateQmonthApprovalStatusMutation } from '../../../redux/services/hatvQmonthDetailsAPI';
import { withUtag } from '../../../utils/analytics';
import i18n from '../../../i18n';

export interface IHATVQualificationActionProps {
  aboNumber: string;
  affiliateCode: string;
  approvedFor: string;
  approvalType: number;
  qualificationStatus: number;
  closingMonth?: number;
  onClose: () => void;
  setIsStatusUpdate?: (apiStatus: string) => void;
  currentAward?: string;
  currentStatus?: number;
  currentAwardCode?: string | number | null;
  selectedAbos?: TableRowData[];
}

const HATVQualificationActionModal: React.FC<IHATVQualificationActionProps> = ({
  aboNumber,
  affiliateCode,
  approvedFor,
  approvalType,
  qualificationStatus,
  closingMonth,
  onClose,
  setIsStatusUpdate,
  currentAward,
  currentStatus,
  currentAwardCode,
  selectedAbos,
}) => {
  const { t } = useTranslation();
  const fixedT = i18n.getFixedT('en');
  const isBulk = !!selectedAbos && selectedAbos.length > 0;

  const [selectedApprovalType, setSelectedApprovalType] = useState<number>(
    isBulk ? HATV_QUALIFICATION_ACTION.APPROVE : approvalType
  );
  const [timePeriod, setTimePeriod] = useState<number | undefined>(closingMonth);

  const [textareaValue, setTextareaValue] = useState<string>('');
  const [isEditable, setIsEditable] = useState(false);
  const [saveEnable, setSaveEnable] = useState(false);
  const [updateDisable, setUpdateDisable] = useState(false);

  const [updateQmonthApprovalApprovalStatus, { isLoading: isUpdating }] =
    useUpdateQmonthApprovalStatusMutation();

  const { aboNumber: urlParamAboNumber } = useProfileParams();
  const isDetailsPage = !!(urlParamAboNumber && urlParamAboNumber?.toString() === aboNumber);

  const textAreaChangeHandler = (editorData: any) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorData, 'text/html');
    const rowNote = doc.body.textContent || '';
    setSaveEnable(rowNote.trim().length > 0);
    editorData === '<p><br></p>' ? setTextareaValue('') : setTextareaValue(editorData);
  };

  const handleClickCancel = () => {
    onClose();
  };

  const bulkNamesText = useMemo(() => {
    if (!selectedAbos) return '';
    const names = selectedAbos.map((abo) => abo.name);
    if (names.length <= 3) return names.join(', ');
    const firstThree = names.slice(0, 3).join(', ');
    const othersCount = names.length - 3;
    return `${firstThree}, ${t('and')} ${othersCount} ${t('others')}`;
  }, [selectedAbos, t]);

  const flaggedAbosText = useMemo(() => {
    if (!selectedAbos) return null;
    const flaggedNames = selectedAbos
      .filter((abo) => abo.flag?.toLowerCase() === 'red' || abo.flag?.toLowerCase() === 'yellow')
      .map((abo) => abo.name);
    
    if (flaggedNames.length === 0) return null;
    
    let namesStr = '';
    if (flaggedNames.length === 1) {
      namesStr = flaggedNames[0];
    } else {
      const last = flaggedNames.pop();
      namesStr = `${flaggedNames.join(', ')} ${t('and')} ${last}`;
    }
    
    return t('flagged_q_month_auto_update_msg', { names: namesStr });
  }, [selectedAbos, t]);

  const handleClickSave = async () => {
    if (isBulk && !saveEnable) return;
    setUpdateDisable(true);

    if (!isBulk) {
      updateQmonthApprovalApprovalStatus({
        aboNumber,
        affiliateCode,
        payload: {
          closingMonth: timePeriod?.toString() || '',
          approvalStatus: selectedApprovalType,
          note: textareaValue,
        },
      })
        .unwrap()
        .then(() => {
          setIsStatusUpdate && setIsStatusUpdate('success');
          onClose();

          const currentApprovalStatus = currentStatus ? fixedT(`q_month_approval_status.${currentStatus}`) : '-';
          const awardStatusTrackData = {
            event_name: 'award_status_change',
            abo_account: aboNumber,
            current_award: isDetailsPage ? getFromLocalStorage<any>('currentAward')?.currentAwardName || '' : currentAward || '',
            award_status: currentApprovalStatus,
            to_status: selectedApprovalType === 1 ? 'Approve' : selectedApprovalType === 2 ? 'On Hold' : 'Deny',
            approval_type: isDetailsPage ? 'q_month_detail' : 'q_month_dashboard',
          };
          withUtag().then((utag) => utag.link(awardStatusTrackData));
        })
        .catch((err) => {
          setIsStatusUpdate && setIsStatusUpdate('fail');
        })
        .finally(() => {
          setUpdateDisable(false);
        });
    }
  };

  return (
    <div className={`approval_container ${styles.approval_container}`}>
      <div className={`${styles.modal_title}`}>{t('update_qualification_status')}</div>
      <div className={`${styles.approvedForSection}`}>
        {isBulk ? (
          <>
            <span className={styles.bold_text}>{t('Approve')}</span>
            {` ${t('award_status_for')}`}
          </>
        ) : (
          t('update_qualification_status_for')
        )}
        <span className={`${styles.approvedForText}`}>{isBulk ? ` ${bulkNamesText}` : ` ${approvedFor}`}.</span>
      </div>
      
      {isBulk && flaggedAbosText && (
        <div className={styles.flagged_msg}>
          {flaggedAbosText}
        </div>
      )}

      {!isBulk && (
        <div className={styles.dropdown_container}>
          <ApprovalTypeDropdown
            selectedApprovalType={selectedApprovalType}
            qualificationStatus={qualificationStatus}
            onApprovalChange={setSelectedApprovalType}
          />
          <TimePeriodDropdown timePeriod={timePeriod} onTimePeriodChange={setTimePeriod} />
        </div>
      )}

      <div className={styles.button_input_wrapper}>
        <div className={styles.formatter_wrapper}>
          <FormatterBar
            id="notes"
            editorData={textareaValue}
            handleDataChange={textAreaChangeHandler}
            section={'htvApproval'}
            placeholder={t('add_note_placeholder')}
            isContentEditable={isEditable}
            maxCharacter={1000}
          />
        </div>
        <div className={styles.btn_wrapper}>
          <Button
            id="cancelButton"
            onClick={handleClickCancel}
            label={t('cancel_btn')}
            style="unfilled"
            className={styles.btn_styles}
          />
          <Button
            id="saveButton"
            onClick={handleClickSave}
            label={t('update_status_btn')}
            style="filled"
            isDisabled={updateDisable || isUpdating || (isBulk && !saveEnable) || (!isBulk && (selectedApprovalType === null || timePeriod == null))}
            className={styles.btn_styles}
          />
        </div>
      </div>
    </div>
  );
};

const ApprovalTypeDropdown = ({
  selectedApprovalType,
  qualificationStatus,
  onApprovalChange,
}: {
  selectedApprovalType: number;
  qualificationStatus: number;
  onApprovalChange: (type: number) => void;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const approvalDropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(approvalDropdownRef, () => setIsOpen(false));
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const dropdownArrow = currentTheme === 'light' ? dropdownArrowLight : dropdownArrowDark;

  return (
    <div className={`${styles.input_wrapper}`} ref={approvalDropdownRef}>
      <input
        type="text"
        className={`approval_input ${styles.dropdown_input} ${isOpen ? 'selected_input' : ''}`}
        value={selectedApprovalType ? t(`hatv_q_month_approval_actions.${selectedApprovalType}`) : ''}
        readOnly
        onClick={() => setIsOpen(!isOpen)}
        placeholder={`${t('qualification_status_placeholder')}*`}
      />
      <img
        src={dropdownArrow}
        alt="Downkey"
        className={`${styles.arrow_icon} ${isOpen ? styles.rotate : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      />
      {isOpen && (
        <div className={`dropdown_menu ${styles.dropdown_menu}`}>
          {Object.values(HATV_QUALIFICATION_ACTION).map((action) =>
            qualificationStatus !== action ? (
              <div
                key={action}
                className={`dropdown_item ${selectedApprovalType === action ? 'dropdown_selected_item' : ''} ${styles.dropdown_item}`}
                onClick={() => { onApprovalChange(action); setIsOpen(false); }}
              >
                {t(`hatv_q_month_approval_actions.${action}`)}
              </div>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

const TimePeriodDropdown = ({
  timePeriod,
  onTimePeriodChange,
}: {
  timePeriod: number | undefined;
  onTimePeriodChange: (type: number) => void;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setIsOpen(false));
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const dropdownArrow = currentTheme === 'light' ? dropdownArrowLight : dropdownArrowDark;

  const month = timePeriod && parseInt(timePeriod.toString()?.slice(4, 6), 10) - 1;
  const timePeriodOptions = getTimePeriodOptionsTillCurrent(Number(month));

  return (
    <div className={`${styles.input_wrapper}`} ref={dropdownRef}>
      <input
        type="text"
        className={`approval_input ${styles.dropdown_input} ${isOpen ? 'selected_input' : ''}`}
        value={timePeriodOptions.find((tp) => tp.value === timePeriod)?.label}
        readOnly
        onClick={() => setIsOpen(!isOpen)}
        placeholder={`${t('time_period_placeholder')}*`}
      />
      <img
        src={dropdownArrow}
        alt="Downkey"
        className={`${styles.arrow_icon} ${isOpen ? styles.rotate : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      />
      {isOpen && (
        <div className={`dropdown_menu ${styles.dropdown_menu}`}>
          {timePeriodOptions.map(({ label, value }) => (
            <div
              key={value}
              className={`dropdown_item ${timePeriod === value ? 'dropdown_selected_item' : ''} ${styles.dropdown_item}`}
              onClick={() => { onTimePeriodChange(value); setIsOpen(false); }}
            >
              {label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HATVQualificationActionModal;
